import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react';

const ToastContext = createContext(null);

const TYPE_CFG = {
  success: { color: 'var(--green)', dim: 'var(--green-dim)', Icon: CheckCircle2 },
  error:   { color: 'var(--red)',   dim: 'var(--red-dim)',   Icon: AlertCircle },
  info:    { color: 'var(--cyan)',  dim: 'var(--cyan-dim)',  Icon: Info },
};

let _id = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const dismiss = useCallback((id) => {
    setToasts(t => t.filter(x => x.id !== id));
    if (timers.current[id]) { clearTimeout(timers.current[id]); delete timers.current[id]; }
  }, []);

  const push = useCallback((message, type = 'info', duration = 4500) => {
    if (!message) return;
    const id = ++_id;
    setToasts(t => [...t, { id, message, type }]);
    timers.current[id] = setTimeout(() => dismiss(id), duration);
    return id;
  }, [dismiss]);

  // Bridge: moduli non-React (es. api.js) lanciano window event "eden-toast"
  useEffect(() => {
    const handler = (e) => push(e.detail?.message, e.detail?.type || 'info', e.detail?.duration);
    window.addEventListener('eden-toast', handler);
    return () => window.removeEventListener('eden-toast', handler);
  }, [push]);

  const api = {
    toast: push,
    success: (m, d) => push(m, 'success', d),
    error:   (m, d) => push(m, 'error', d),
    info:    (m, d) => push(m, 'info', d),
    dismiss,
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* Container — top-right, sopra ogni overlay */}
      <div style={{
        position: 'fixed', top: 16, right: 16, zIndex: 9999,
        display: 'flex', flexDirection: 'column', gap: 8,
        maxWidth: 'min(380px, calc(100vw - 32px))', pointerEvents: 'none',
      }} aria-live="polite" role="status">
        {toasts.map(t => {
          const cfg = TYPE_CFG[t.type] || TYPE_CFG.info;
          const { Icon } = cfg;
          return (
            <div key={t.id} className="eden-toast-in" style={{
              pointerEvents: 'auto',
              display: 'flex', alignItems: 'flex-start', gap: 10,
              background: 'var(--bg2, #14141c)', border: `1px solid ${cfg.color}`,
              borderLeft: `3px solid ${cfg.color}`, borderRadius: 10,
              padding: '11px 13px', boxShadow: '0 8px 28px rgba(0,0,0,0.45)',
            }}>
              <Icon size={16} color={cfg.color} style={{ flexShrink: 0, marginTop: 1 }} />
              <span style={{ flex: 1, fontSize: 13, color: 'var(--text)', lineHeight: 1.45, wordBreak: 'break-word' }}>
                {t.message}
              </span>
              <button onClick={() => dismiss(t.id)} aria-label="Chiudi notifica"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)', padding: 0, display: 'flex', flexShrink: 0 }}>
                <X size={14} />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => {
  const ctx = useContext(ToastContext);
  // Fallback no-op se usato fuori dal provider (evita crash)
  return ctx || { toast: () => {}, success: () => {}, error: () => {}, info: () => {}, dismiss: () => {} };
};
